import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Brain, Target, TrendingUp, Calendar, CheckCircle2, Clock, BookOpen,
  Zap, Award, ChevronRight, Play, BarChart3, Lightbulb, Loader2,
  Star, Trophy, Sparkles, Rocket, ArrowRight, ArrowLeft, RefreshCw,
  Flame, AlertTriangle, ThumbsUp, Calculator, BookA, Puzzle, Shapes, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SixteenWeekCalendar from '../components/SixteenWeekCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject icons and colors
const SUBJECT_CONFIG = {
  maths: { icon: Calculator, color: 'blue', bgColor: 'bg-blue-500/10', textColor: 'text-blue-400' },
  english: { icon: BookA, color: 'green', bgColor: 'bg-green-500/10', textColor: 'text-green-400' },
  verbal_reasoning: { icon: Brain, color: 'purple', bgColor: 'bg-purple-500/10', textColor: 'text-purple-400' },
  non_verbal_reasoning: { icon: Shapes, color: 'orange', bgColor: 'bg-orange-500/10', textColor: 'text-orange-400' }
};

// Classification colors
const CLASSIFICATION_CONFIG = {
  mastery: { color: 'emerald', icon: Trophy, label: 'Mastered', bgColor: 'bg-emerald-500/10', textColor: 'text-emerald-400' },
  comfort: { color: 'blue', icon: ThumbsUp, label: 'Comfortable', bgColor: 'bg-blue-500/10', textColor: 'text-blue-400' },
  growth: { color: 'amber', icon: Target, label: 'Focus Area', bgColor: 'bg-amber-500/10', textColor: 'text-amber-400' }
};

const PersonalizedLearningPath = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('focus');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [topicAnalysis, setTopicAnalysis] = useState(null);
  const [growthZone, setGrowthZone] = useState(null);
  const [learningPlan, setLearningPlan] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  
  // Get user ID from token
  const getUserId = () => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.user_id;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    const userId = getUserId();
    
    if (!userId) {
      toast.error('Please log in to view your learning path');
      navigate('/auth');
      return;
    }

    try {
      const [summaryRes, analysisRes, growthRes, planRes] = await Promise.all([
        axios.get(`${API}/learning-engine/summary/${userId}`),
        axios.get(`${API}/learning-engine/topic-analysis/${userId}`),
        axios.get(`${API}/learning-engine/growth-zone/${userId}`),
        axios.get(`${API}/learning-engine/learning-plan/${userId}`)
      ]);

      setSummary(summaryRes.data);
      setTopicAnalysis(analysisRes.data);
      setGrowthZone(growthRes.data);
      setLearningPlan(planRes.data);
    } catch (error) {
      console.error('Error fetching learning data:', error);
      toast.error('Failed to load learning path data');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskIndex) => {
    const userId = getUserId();
    try {
      await axios.post(`${API}/learning-engine/complete-task?user_id=${userId}&task_index=${taskIndex}`);
      toast.success('Task completed! Keep going!');
      setTaskModalOpen(false);
      fetchAllData(); // Refresh data
    } catch (error) {
      toast.error('Failed to mark task as complete');
    }
  };

  // Handle starting a task
  const handleStartTask = (task, index) => {
    setSelectedTask({ ...task, index });
    setTaskModalOpen(true);
  };

  // Get topic display name
  const getTopicDisplayName = (topic) => {
    return topic?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Topic';
  };

  // Navigate to practice with topic
  const goToPractice = (topic, subject) => {
    // Navigate to My Growth Zone (AI Practice) 
    // The user can select the topic there
    navigate('/ai-practice');
    toast.info(`Select "${getTopicDisplayName(topic)}" in ${subject} to practice`);
  };

  // Navigate to resources
  const goToResources = (topic, subject) => {
    // Navigate to Resources Hub
    navigate('/resources');
    toast.info(`Find ${subject} resources for "${getTopicDisplayName(topic)}"`);
  };

  // No data state
  if (!loading && (!summary?.has_data || !topicAnalysis?.has_data)) {
    return (
      <div className="min-h-screen bg-gray-950 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>

          {/* Empty State */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="inline-flex p-6 rounded-full bg-gradient-to-br from-purple-500/20 to-indigo-500/20 mb-6">
              <Rocket className="w-16 h-16 text-purple-400" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-4">
              Your Learning Path Awaits!
            </h1>
            
            <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
              Complete an assessment to unlock your personalized learning journey with smart recommendations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/ks2-assessment')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90"
                size="lg"
              >
                <Target className="w-5 h-5 mr-2" />
                Take Adaptive Assessment
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/assessment-factory')}
                size="lg"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Try Mock Exam
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your learning path...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <nav className="sticky top-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-400" />
              <span className="font-bold text-white">My Learning Path</span>
            </div>
            
            <Button
              variant="ghost"
              onClick={fetchAllData}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 py-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Overall Strength */}
          <Card className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-300 text-sm">Overall Strength</p>
                  <p className="text-3xl font-bold text-white">{summary?.overall_strength || 0}%</p>
                </div>
                <div className="p-3 rounded-full bg-purple-500/20">
                  <Zap className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Mastery Topics */}
          <Card className="bg-gradient-to-br from-emerald-900/50 to-green-900/50 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-300 text-sm">Mastered</p>
                  <p className="text-3xl font-bold text-white">{summary?.topics_summary?.mastery || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-emerald-500/20">
                  <Trophy className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Comfort Topics */}
          <Card className="bg-gradient-to-br from-blue-900/50 to-cyan-900/50 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-300 text-sm">Comfortable</p>
                  <p className="text-3xl font-bold text-white">{summary?.topics_summary?.comfort || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-500/20">
                  <ThumbsUp className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Growth Topics */}
          <Card className="bg-gradient-to-br from-amber-900/50 to-orange-900/50 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-300 text-sm">Focus Areas</p>
                  <p className="text-3xl font-bold text-white">{summary?.topics_summary?.growth || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-amber-500/20">
                  <Target className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insight Card */}
        {learningPlan?.insight && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-purple-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-purple-500/20 shrink-0">
                    <Lightbulb className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{learningPlan.insight.title}</h3>
                    <p className="text-gray-300 mb-2">{learningPlan.insight.message}</p>
                    <div className="flex items-center gap-2 text-purple-300">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm">{learningPlan.insight.tip}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-900/50 border border-gray-800">
            <TabsTrigger value="focus" className="data-[state=active]:bg-amber-600">
              <Flame className="w-4 h-4 mr-2" />
              Focus Now
            </TabsTrigger>
            <TabsTrigger value="plan" className="data-[state=active]:bg-purple-600">
              <Calendar className="w-4 h-4 mr-2" />
              Your Plan
            </TabsTrigger>
            <TabsTrigger value="topics" className="data-[state=active]:bg-blue-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              All Topics
            </TabsTrigger>
          </TabsList>

          {/* Focus Now Tab */}
          <TabsContent value="focus">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Flame className="w-5 h-5 text-amber-400" />
                  Priority Focus Areas
                </h2>
                <Badge className="bg-amber-600">{growthZone?.focus_topics?.length || 0} topics</Badge>
              </div>
              
              {growthZone?.focus_topics?.length > 0 ? (
                <div className="grid gap-4">
                  {growthZone.focus_topics.map((topic, index) => {
                    const subjectConfig = SUBJECT_CONFIG[topic.subject] || SUBJECT_CONFIG.maths;
                    const Icon = subjectConfig.icon;
                    
                    return (
                      <motion.div
                        key={topic.topic}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card className="bg-gray-900/50 border-gray-800 hover:border-amber-500/50 transition-all">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-4">
                                <div className={`p-2 rounded-lg ${subjectConfig.bgColor}`}>
                                  <Icon className={`w-5 h-5 ${subjectConfig.textColor}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-bold text-white">{topic.topic_name}</h3>
                                    <Badge variant="outline" className="text-xs">
                                      #{index + 1} Priority
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-400 capitalize">{topic.subject.replace('_', ' ')}</p>
                                  
                                  {/* Score bar */}
                                  <div className="mt-3 space-y-1">
                                    <div className="flex justify-between text-xs">
                                      <span className="text-gray-400">Current: {topic.avg_score}%</span>
                                      <span className="text-amber-400">Target: 80%</span>
                                    </div>
                                    <Progress value={topic.avg_score} className="h-2" />
                                  </div>
                                  
                                  {/* Recommendation */}
                                  <div className="mt-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                    <p className="text-xs text-amber-300">
                                      💡 {topic.recommendation?.message}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              <Button
                                size="sm"
                                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90"
                                onClick={() => navigate(`/ai-practice?topic=${topic.topic}&subject=${topic.subject}`)}
                              >
                                <Play className="w-4 h-4 mr-1" />
                                Practice
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <Card className="bg-gray-900/50 border-gray-800">
                  <CardContent className="p-8 text-center">
                    <Trophy className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-white mb-2">No Weak Topics!</h3>
                    <p className="text-gray-400">
                      Great job! You're performing well across all topics. Keep practicing to maintain your skills.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Your Plan Tab - 16-Week Calendar */}
          <TabsContent value="plan">
            <div className="bg-white rounded-xl p-6">
              <SixteenWeekCalendar
                weeklyPlan={learningPlan?.weekly_plan || []}
                focusTopics={growthZone?.focus_topics || []}
                onRefresh={fetchAllData}
                showHeader={true}
              />
            </div>
          </TabsContent>

          {/* All Topics Tab */}
          <TabsContent value="topics">
            <div className="space-y-6">
              {/* Mastery Topics */}
              {topicAnalysis?.mastery?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-bold text-white">Mastered ({topicAnalysis.mastery.length})</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {topicAnalysis.mastery.map((topic) => (
                      <TopicCard key={topic.topic} topic={topic} classification="mastery" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Comfort Topics */}
              {topicAnalysis?.comfort?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <ThumbsUp className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-bold text-white">Comfortable ({topicAnalysis.comfort.length})</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {topicAnalysis.comfort.map((topic) => (
                      <TopicCard key={topic.topic} topic={topic} classification="comfort" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Growth Topics */}
              {topicAnalysis?.growth?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-bold text-white">Focus Areas ({topicAnalysis.growth.length})</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {topicAnalysis.growth.map((topic) => (
                      <TopicCard key={topic.topic} topic={topic} classification="growth" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Task Modal */}
        <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
            {selectedTask && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-xl">
                    <div className={`p-2 rounded-lg ${
                      selectedTask.type === 'revision' ? 'bg-blue-500/20' :
                      selectedTask.type === 'practice' ? 'bg-purple-500/20' : 'bg-amber-500/20'
                    }`}>
                      {selectedTask.type === 'revision' ? (
                        <BookOpen className="w-5 h-5 text-blue-400" />
                      ) : selectedTask.type === 'practice' ? (
                        <Target className="w-5 h-5 text-purple-400" />
                      ) : (
                        <Zap className="w-5 h-5 text-amber-400" />
                      )}
                    </div>
                    {selectedTask.title}
                  </DialogTitle>
                  <DialogDescription className="text-gray-400 pt-2">
                    {selectedTask.description}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {/* Task Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedTask.estimated_time} minutes
                    </span>
                    <Badge variant="outline" className="capitalize">
                      {selectedTask.difficulty}
                    </Badge>
                    <span className="capitalize">Day {selectedTask.day}</span>
                  </div>

                  {/* Task Type Specific Content */}
                  {selectedTask.type === 'revision' && (
                    <Card className="bg-blue-500/10 border-blue-500/30">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-blue-300 mb-2 flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Concept Revision
                        </h4>
                        <p className="text-gray-300 text-sm mb-3">
                          Review the key concepts and work through example problems. 
                          Focus on understanding the underlying principles.
                        </p>
                        <div className="space-y-2 text-sm text-gray-400">
                          <p>Tips for effective revision:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Read through explanations carefully</li>
                            <li>Try the worked examples yourself</li>
                            <li>Make notes on key formulas/rules</li>
                            <li>Identify what confuses you</li>
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {selectedTask.type === 'practice' && (
                    <Card className="bg-purple-500/10 border-purple-500/30">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-purple-300 mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Practice Questions
                        </h4>
                        <p className="text-gray-300 text-sm mb-3">
                          Work through practice questions to reinforce your understanding. 
                          Take your time and focus on accuracy.
                        </p>
                        <div className="space-y-2 text-sm text-gray-400">
                          <p>Practice tips:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Read each question carefully</li>
                            <li>Show your working</li>
                            <li>Check your answers</li>
                            <li>Review mistakes to learn from them</li>
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {selectedTask.type === 'quiz' && (
                    <Card className="bg-amber-500/10 border-amber-500/30">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-amber-300 mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Timed Quiz
                        </h4>
                        <p className="text-gray-300 text-sm mb-3">
                          Test yourself under timed conditions to build exam skills.
                          Try to work quickly but accurately.
                        </p>
                        <div className="space-y-2 text-sm text-gray-400">
                          <p>Quiz strategy:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Don't spend too long on one question</li>
                            <li>Make educated guesses if stuck</li>
                            <li>Flag questions to review</li>
                            <li>Stay calm under pressure</li>
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    {selectedTask.type === 'revision' ? (
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          goToResources(selectedTask.topic, selectedTask.subject);
                          setTaskModalOpen(false);
                        }}
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Go to Resources
                      </Button>
                    ) : (
                      <Button
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          goToPractice(selectedTask.topic, selectedTask.subject);
                          setTaskModalOpen(false);
                        }}
                      >
                        <Target className="w-4 h-4 mr-2" />
                        Go to Practice
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => handleCompleteTask(selectedTask.index)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark Done
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// Topic Card Component
const TopicCard = ({ topic, classification }) => {
  const config = CLASSIFICATION_CONFIG[classification];
  const subjectConfig = SUBJECT_CONFIG[topic.subject] || SUBJECT_CONFIG.maths;
  const SubjectIcon = subjectConfig.icon;
  
  return (
    <Card className="bg-gray-900/50 border-gray-800">
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded ${subjectConfig.bgColor}`}>
              <SubjectIcon className={`w-4 h-4 ${subjectConfig.textColor}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-white">{topic.topic_name}</p>
              <p className="text-xs text-gray-500 capitalize">{topic.subject.replace('_', ' ')}</p>
            </div>
          </div>
          <div className="text-right">
            <Badge className={`${config.bgColor} ${config.textColor} border-0`}>
              {topic.avg_score}%
            </Badge>
            <p className="text-xs text-gray-500 mt-1">{topic.attempts} attempts</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalizedLearningPath;
